import Template from './components/template'
import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { user } from './lib/api'
import { User } from './lib/types'
// import Modal from './components/modal'
import { Button, Modal, Search, Select, SelectItem, TextInput, Toggle } from '@carbon/react'
import { Add } from '@carbon/icons-react'
import { useDebouncedCallback } from 'use-debounce'
import { userStatusOptions } from './workflows/util'
import Loading from './components/loading'
import auth from './lib/auth'

function EditUserModal({
    open,
    userToUpdate,
    onClose,
}: {
    open: boolean
    userToUpdate: User
    onClose: () => void
}) {
    const [email, setEmail] = useState(userToUpdate.email)
    const [password, setPassword] = useState('')
    const [name, setName] = useState(userToUpdate.name)
    const [role, setRole] = useState(userToUpdate.role)
    const [status, setStatus] = useState(userToUpdate.status)
    const [loading, setLoading] = useState(false)
    const isLead = useMemo(() => auth.checkRole('lead'), [])

    async function editUser(e: FormEvent) {
        e.preventDefault()
        if (userToUpdate.id) {
            setLoading(true)
            let toUpdate: {
                name?: string
                password?: string
                email?: string
                role?: string
                status?: number
            } = {}
            if (password) toUpdate.password = password
            if (name !== userToUpdate.name) toUpdate.name = name
            if (email !== userToUpdate.email) toUpdate.email = email
            if (role !== userToUpdate.role) toUpdate.role = role
            if (status !== userToUpdate.status) toUpdate.status = status
            await user.update(userToUpdate.id, toUpdate)
            setLoading(false)
        }
        onClose()
    }

    return (
        <Modal
            open={open}
            modalHeading='Edit User'
            primaryButtonDisabled={!name || !email || loading}
            primaryButtonText='Submit'
            secondaryButtonText='Cancel'
            onRequestSubmit={editUser}
            onRequestClose={onClose}
        >
            <TextInput
                id='name'
                labelText='Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <TextInput
                id='email'
                labelText='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <TextInput
                id='password'
                labelText='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <div style={{ width: '300px' }}>
                <Select
                    id={'role-select'}
                    labelText='Role'
                    value={role}
                    disabled={isLead}
                    onChange={(e) => setRole(e.target.value)}
                >
                    <SelectItem value='' text='User' />
                    <SelectItem value='admin' text='Admin' />
                    <SelectItem value='lead' text='Lead' />
                </Select>
            </div>
            <Toggle
                id='status'
                labelText='Status'
                labelA='Inactive'
                labelB='Active'
                value={status}
                defaultToggled={status ? true : false}
                onToggle={(value) => setStatus(value ? 1 : 0)}
            />
        </Modal>
    )
}

function AddUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [name, setName] = useState('')
    const [role, setRole] = useState('')
    const [status, setStatus] = useState(1)
    const [loading, setLoading] = useState(false)
    const isLead = useMemo(() => auth.checkRole('lead'), [])

    async function addUser(e: FormEvent) {
        e.preventDefault()
        setLoading(true)
        await user.create({ name, email, password, role, status, sub: 1, exp: 1 })
        setLoading(false)
        onClose()
    }

    return (
        <Modal
            open={open}
            modalHeading='Add User'
            primaryButtonDisabled={!name || !email || !password || loading}
            primaryButtonText='Submit'
            secondaryButtonText='Cancel'
            onRequestSubmit={addUser}
            onRequestClose={onClose}
        >
            <TextInput
                id='name'
                labelText='Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <TextInput
                id='email'
                labelText='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <TextInput
                id='password'
                labelText='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <div style={{ width: '300px' }}>
                <Select
                    id={'role-select'}
                    size='md'
                    labelText='Role'
                    value={role}
                    disabled={isLead}
                    onChange={(e) => setRole(e.target.value)}
                >
                    <SelectItem value='' text='User' />
                    <SelectItem value='admin' text='Admin' />
                    <SelectItem value='lead' text='Lead' />
                </Select>
            </div>
            <Toggle
                id='status'
                labelText='Status'
                labelA='Inactive'
                labelB='Active'
                value={status}
                onToggle={(value) => setStatus(value ? 1 : 0)}
            />
        </Modal>
    )
}

export default function AdminPage() {
    const [search, setSearch] = useState('')
    const [users, setUsers] = useState<User[]>([])
    const [addUserOpen, setAddUserOpen] = useState(false)
    const [editUserOpen, setEditUserOpen] = useState(false)
    const [editingUser, setEditingUser] = useState<User>()
    const [filter, setFilter] = useState('')
    const [loading, setLoading] = useState(false)
    const isLead = useMemo(() => auth.checkRole('lead'), [])

    const updateFilter = useCallback((value: string) => setFilter(value), [])
    const handleSearch = useDebouncedCallback((value: string) => {
        setSearch(value)
    }, 2000)

    useEffect(() => {
        setLoading(true)
        user.list(search, filter).then((u: User[]) => {
            setUsers(u)
            setLoading(false)
        })
    }, [addUserOpen, editUserOpen, search, filter])

    function openEditUser(user: User) {
        setEditingUser(user)
        setEditUserOpen(true)
    }

    function canEditUser(targetUserRole: string) {
        if (isLead) {
            if (targetUserRole === '') {
                return true
            }
            return false
        } else {
            return true
        }
    }

    return (
        <Template>
            <div className='container mx-auto mt-5'>
                <h1>Admin</h1>
                <div className='flex flex-row justify-between items-center my-2'>
                    <h3 className='text-lg font-medium'>Users</h3>
                    {loading && <Loading />}
                    <div className='flex flex-row items-center'>
                        <Search
                            size='sm'
                            placeholder='Search users'
                            labelText='Search'
                            style={{ borderRadius: 0, boxShadow: 'initial' }}
                            closeButtonLabelText='Clear search input'
                            id='user-search'
                            onChange={(e) => handleSearch(e.target.value)}
                        />

                        <div className='flex flex-row items-center mx-2'>
                            <Select
                                id='user-filter'
                                labelText=''
                                className='bg-[#F4F4F4]'
                                hideLabel={true}
                                inline={true}
                                size='sm'
                                onChange={(e) => updateFilter(e.target.value)}
                            >
                                {userStatusOptions.map(({ id, value, label }) => (
                                    <SelectItem key={id} value={value} text={label} />
                                ))}
                            </Select>
                        </div>
                        <Button onClick={() => setAddUserOpen(true)} renderIcon={Add} size='sm'>
                            Add User
                        </Button>
                    </div>
                </div>
                <table className='table-auto' style={{ maxHeight: '500px' }}>
                    <thead>
                        <tr>
                            <th>Id</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id}>
                                <td>{u.id}</td>
                                <td>{u.name}</td>
                                <td>{u.email}</td>
                                <td>{u.role}</td>
                                <td>{u.status ? 'Active' : 'Inactive'}</td>
                                <td className='w-px whitespace-nowrap'>
                                    <button
                                        onClick={() => {
                                            openEditUser(u)
                                        }}
                                        disabled={!canEditUser(u.role)}
                                        className=''
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <hr className='my-10' />
                {addUserOpen && (
                    <AddUserModal
                        open={addUserOpen}
                        onClose={() => {
                            setAddUserOpen(false)
                        }}
                    />
                )}
                {editUserOpen && editingUser && (
                    <EditUserModal
                        open={editUserOpen}
                        userToUpdate={editingUser}
                        onClose={() => {
                            setEditUserOpen(false)
                        }}
                    />
                )}
            </div>
        </Template>
    )
}
